import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { GroupRenderer } from './GroupRenderer';
import { NODE_TYPE_GROUPS, REPORT_FORM_VALIDATION } from './constants';

interface SearchableGroupListProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedSections: string[];
  onGroupToggle: (groupName: string, items: { id: string; name: string }[], isTopicGroup?: boolean) => void;
  onSectionToggle: (section: string) => void;
}

export const SearchableGroupList: React.FC<SearchableGroupListProps> = ({
  searchQuery,
  onSearchChange,
  selectedSections,
  onGroupToggle,
  onSectionToggle,
}) => {
  // Ensure selectedSections is always an array
  const sections = Array.isArray(selectedSections) ? selectedSections : [];
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const maxLength = REPORT_FORM_VALIDATION.searchQuery.maxLength;
  
  // Sync local state with prop
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);
  
  // Handle search input change with validation
  const handleSearchChange = (value: string) => {
    // Validate and sanitize the input, but preserve spaces
    const sanitizedValue = value.length > REPORT_FORM_VALIDATION.searchQuery.maxLength
      ? value.substring(0, REPORT_FORM_VALIDATION.searchQuery.maxLength)
      : value;
    
    setLocalSearchQuery(sanitizedValue);
    onSearchChange(sanitizedValue);
  };
  
  // Handle keyboard events for search field
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleSearchChange('');
    }
  };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded overflow-y-auto" style={{ height: '300px' }}>
      {/* Sticky search field */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-700 shadow-sm">
        <div className="relative">
          <div className="relative">
            <input
              type="text"
              placeholder="Search sections..."
              value={localSearchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              maxLength={maxLength}
              className="w-full pl-8 pr-6 py-2 text-sm border-b border-gray-300 dark:border-gray-600 rounded-t focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <span className="absolute right-8 top-2 text-xs text-gray-400">
              {localSearchQuery.length}/{maxLength}
            </span>
          </div>
          <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
          {localSearchQuery && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-2 h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X />
            </motion.button>
          )}
        </div>
      </div>

      {/* Render group blocks */}
      <GroupRenderer
        groupKey="SOURCES"
        groupLabel="SOURCES"
        items={NODE_TYPE_GROUPS.SOURCES}
        selectedSections={sections}
        searchQuery={searchQuery}
        onGroupToggle={onGroupToggle}
        onSectionToggle={onSectionToggle}
      />
      <GroupRenderer
        groupKey="ARCHITECTURE"
        groupLabel="ARCHITECTURE"
        items={NODE_TYPE_GROUPS.ARCHITECTURE}
        selectedSections={sections}
        searchQuery={searchQuery}
        onGroupToggle={onGroupToggle}
        onSectionToggle={onSectionToggle}
      />
      <GroupRenderer
        groupKey="MODULES"
        groupLabel="MODULES"
        items={NODE_TYPE_GROUPS.MODULES}
        selectedSections={sections}
        searchQuery={searchQuery}
        onGroupToggle={onGroupToggle}
        onSectionToggle={onSectionToggle}
      />
      <GroupRenderer
        groupKey="TOPICS"
        groupLabel="TOPICS"
        items={NODE_TYPE_GROUPS.TOPICS}
        selectedSections={sections}
        searchQuery={searchQuery}
        onGroupToggle={onGroupToggle}
        onSectionToggle={onSectionToggle}
        isTopicGroup={true}
      />
      {NODE_TYPE_GROUPS.DESTINATIONS.length > 0 && (
        <GroupRenderer
          groupKey="DESTINATIONS"
          groupLabel="DESTINATIONS"
          items={NODE_TYPE_GROUPS.DESTINATIONS}
          selectedSections={sections}
          searchQuery={searchQuery}
          onGroupToggle={onGroupToggle}
          onSectionToggle={onSectionToggle}
        />
      )}
    </div>
  );
};
